/*
 * searchOverlay — Alpine component for the site search overlay (.mh-search).
 *
 * Single global overlay mounted once in layout.antlers.html OUTSIDE #swup so it
 * persists across Swup page transitions. Opens when any [data-search-trigger]
 * element (the nav + mobile-drawer search buttons) is clicked anywhere on the
 * page, via a document-level delegated listener (survives Swup swaps).
 *
 * Searching: the input is debounced; each query hits GET /api/search?q=… and
 * the results render as plain links. Clicking a result is a normal navigation —
 * Swup intercepts it, so the overlay's @click close + the page swap compose.
 * In-flight requests are aborted when a newer keystroke supersedes them.
 *
 * Focus + keyboard:
 *   - Open: focus the input (x-trap.inert.noscroll in the partial handles trap +
 *     scroll lock); remember the trigger to restore focus on close.
 *   - Escape / backdrop / close button close (wired in the partial).
 *   - Arrow up/down move the active result; Enter follows it.
 *   - The "Clear" lozenge empties the field and keeps the overlay open — it is
 *     labelled rather than an ✕ so it can't be mistaken for the close control.
 *
 * The document listener is installed in init() and removed in destroy() so a
 * re-init never double-binds.
 */
export default function searchOverlay() {
    return {
        open: false,
        query: '',
        results: [],
        loading: false,
        searched: false,
        active: -1,
        _lastTrigger: null,
        _onDocClick: null,
        _debounce: null,
        _controller: null,

        init() {
            this._onDocClick = (event) => {
                const trigger = event.target.closest('[data-search-trigger]');
                if (!trigger) {
                    return;
                }
                event.preventDefault();
                this._lastTrigger = trigger;
                this.show();
            };
            document.addEventListener('click', this._onDocClick);
        },

        destroy() {
            if (this._onDocClick) {
                document.removeEventListener('click', this._onDocClick);
                this._onDocClick = null;
            }
            clearTimeout(this._debounce);
            this._controller?.abort();
        },

        show() {
            this.open = true;
            this.$nextTick(() => this.$refs.input?.focus());
        },

        close() {
            this.open = false;
            const trigger = this._lastTrigger;
            this._lastTrigger = null;
            if (trigger) {
                this.$nextTick(() => trigger.focus());
            }
        },

        /**
         * Empty the field without closing the overlay — the "Clear" lozenge in
         * the input row. Focus returns to the input so typing can continue.
         */
        clear() {
            clearTimeout(this._debounce);
            this._controller?.abort();
            this.query = '';
            this.results = [];
            this.loading = false;
            this.searched = false;
            this.active = -1;
            this.$refs.input?.focus();
        },

        /** Debounced input handler. */
        onInput() {
            clearTimeout(this._debounce);
            this.active = -1;
            const q = this.query.trim();

            if (q.length < 2) {
                this._controller?.abort();
                this.results = [];
                this.loading = false;
                this.searched = false;
                return;
            }

            this.loading = true;
            this._debounce = setTimeout(() => this.run(q), 200);
        },

        async run(q) {
            this._controller?.abort();
            this._controller = new AbortController();

            try {
                const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, {
                    headers: { Accept: 'application/json' },
                    signal: this._controller.signal,
                });
                if (!res.ok) {
                    throw new Error(`search ${res.status}`);
                }
                const data = await res.json();
                this.results = Array.isArray(data.results) ? data.results : [];
                this.searched = true;
            } catch (e) {
                if (e.name !== 'AbortError') {
                    this.results = [];
                    this.searched = true;
                }
            } finally {
                this.loading = false;
            }
        },

        move(delta) {
            if (!this.results.length) {
                return;
            }
            this.active = (this.active + delta + this.results.length) % this.results.length;
        },

        // Highlight the hovered result. Named method because the CSP Alpine build
        // rejects the inline assignment `active = i` in the @mouseenter binding.
        setActive(index) {
            this.active = index;
        },

        follow() {
            const item = this.results[this.active];
            if (item?.url) {
                window.location.assign(item.url);
            }
        },
    };
}
